import {Injectable} from '@angular/core';
import Dexie, {Table} from 'dexie';
import {catchError, from, Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {SpatialSnapshotsModel} from './spatial-snapshot.model';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class SpatialSnapshotStore extends Dexie {
    title = 'Spatial Snapshot Store';

    storeName = 'snapshots';

    snapshotIds = [];

    baseSnapshotId = '';

    snapshots!: Table<SpatialSnapshotsModel, string>;

    constructor() {
        super('planPortalDb');

        this.version(20000).stores({snapshots: '&id,snapshots'});

        // Must retain declaration of previous versions for Dexie < 3.0
        [1, 11070, 11080, 11090, 11100, 11110, 11120, 11130.5, 11150, 11170, 11190, 11230, 11230.5, 11270, 11290, 11290.5, 11300].forEach(v =>
            this.version(v).stores({snapshots: '&id,snapshots'})
        );

        window.addEventListener('unhandledrejection', (err: any) => {
            // Catch all uncaught DB-related errors and exceptions
            console.error('db error err: ', err);
        });
    }

    /**
     * Setup DB
     * @return {Observable} that resolves when DB is set up
     */
    setupDb(planGuid: string): Observable<any> {
        console.debug(`${this.title} - setupDb()`);

        const promise = new Promise(() => {});

        this.open();

        this.on('ready', () => {
            console.debug(`${this.title} - setupDb() - database ready`);
            this.clearAllSnapshots()
                .pipe(switchMap(() => this.updateSnapshotIds(planGuid)))
                .subscribe(() => Promise.resolve(promise));
        });

        return of(promise);
    }

    /**
     * Create snapshot
     * @return {Observable} that resolves with created snapshot id {string}
     */
    createSnapshot(snapshotObject: any): Observable<any> {
        console.debug(`${this.title} - createSnapshot()`, JSON.stringify(snapshotObject));

        return this.getAllPlanSnapshots(snapshotObject.id).pipe(
            switchMap((response: any) => {
                if (response) {
                    // snapshot plan object exists - just need to add snapshot to it
                    response.snapshots.push(snapshotObject.snapshots[0]);
                    return this.snapshots.put(response);
                } else {
                    return this.snapshots.put(snapshotObject);
                }
            }),
            switchMap(() => {
                this.baseSnapshotId = snapshotObject.snapshots[0].datetime;
                return this.updateSnapshotIds(snapshotObject.id);
            }),
            catchError((reason: any) => {
                console.error(`${this.title} - createSnapshot() - error: ${reason}`);
                return reason;
            })
        );
    }

    /**
     * Get all the plan snapshots
     * @param {string} planGuid - the plan id
     * @return {Observable} that resolves with the retrieved snapshot {Object}
     */
    getAllPlanSnapshots(planGuid: string): Observable<SpatialSnapshotsModel> {
        console.debug(`${this.title}  - getAllPlanSnapshots(${planGuid})`);
        return new Observable(subscriber => {
            this.snapshots
                .get(planGuid)
                .then(value => {
                    subscriber.next(value);
                    subscriber.complete();
                })
                .catch(error => subscriber.error(error));
        });
    }

    /**
     * Get snapshot count
     * @return {Observable} that resolves with the snapshot count {number}
     */
    getSnapshotCount(planGuid: string): Observable<number> {
        console.debug(`${this.title} - getSnapshotCount()`);

        return this.getAllPlanSnapshots(planGuid).pipe(
            switchMap((response: SpatialSnapshotsModel) => {
                if (response) {
                    const snapshotsForThisPlan = response.snapshots.map(f => ({datetime: f.datetime, layers: f.layers}));
                    return of(snapshotsForThisPlan.length);
                } else {
                    return of(0);
                }
            })
        );
    }

    /**
     * Clear snapshot
     * @param {string} planGuid - the plan guid
     * @param {string} snapshotId - the snapshot id
     * @return {Observable} that resolves when snapshot is removed
     */
    clearSnapshot(planGuid: string, snapshotId: string): Observable<any> {
        console.debug(`${this.title} - clearSnapshot()`);

        return this.getAllPlanSnapshots(planGuid).pipe(
            switchMap((response: SpatialSnapshotsModel) => {
                if (response) {
                    response.snapshots = response.snapshots
                        .filter(f => f.datetime !== snapshotId)
                        .map(f => ({datetime: f.datetime, layers: f.layers}));

                    return from(
                        this.snapshots.put(response, planGuid).then(
                            () => this.updateSnapshotIds(planGuid),
                            reason => console.error(`${this.title} - clearSnapshot() - error: ${reason}`)
                        )
                    );
                }
                return of(null);
            })
        );
    }

    /**
     * Clear all snapshots
     * @return {Observable} that resolves when all snapshots are cleared
     */
    clearAllSnapshots(): Observable<any> {
        console.debug(`${this.title} - clearAllSnapshots()`);

        return of(
            this.snapshots.clear().then(() => {
                this.snapshotIds.length = 0;
                this.baseSnapshotId = '';
            })
        );
    }

    private updateSnapshotIds(planGuid: string) {
        console.debug(`${this.title} - updateSnapshotIds()`);

        return of(
            this.snapshots.toCollection().each((plan: any) => {
                this.snapshotIds.length = 0;

                if (plan.id === planGuid) {
                    const snapshotIds = plan.snapshots
                        .map((planSnapshot: any) => planSnapshot.datetime.toString())
                        .sort((a, b) => {
                            const aDate = Number(a.replace(/[-: ]/g, '')); //replace dash, colon and space to make number
                            const bDate = Number(b.replace(/[-: ]/g, ''));
                            return bDate - aDate;
                        });
                    this.snapshotIds.push(...snapshotIds);

                    if (!this.snapshotIds.includes(this.baseSnapshotId)) {
                        this.baseSnapshotId = this.snapshotIds[0] || '';
                    }
                }
            })
        );
    }
}
